import { useState, useEffect } from 'react';
import { Button, TextButton } from '@safetyhub/ui';
import { verificationApi, extractApiError } from '../lib/auth';
import { formatCountdown } from '../lib/countdown';

interface PhoneVerificationProps {
  phone: string;
  onPhoneChange: (phone: string) => void;
  onVerified: (verificationToken: string) => void;
  disabled?: boolean;
  readOnly?: boolean; // 초대 등으로 전화번호가 고정된 경우
  skipDuplicateCheck?: boolean; // 프로필 수정 등에서 중복 체크 생략
}

export default function PhoneVerification({
  phone,
  onPhoneChange,
  onVerified,
  disabled = false,
  readOnly = false,
  skipDuplicateCheck = false,
}: PhoneVerificationProps) {
  const [step, setStep] = useState<'input' | 'verify' | 'done'>('input');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [phoneAvailable, setPhoneAvailable] = useState<boolean | null>(null);

  // 타이머
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 전화번호 정규화
  const normalizePhone = (value: string) => {
    return value.replace(/[^0-9]/g, '');
  };

  // 전화번호 포맷팅
  const formatPhone = (value: string) => {
    const cleaned = normalizePhone(value);
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 7) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`;
  };

  // 전화번호 유효성 검사
  const isValidPhone = (value: string) => {
    const cleaned = normalizePhone(value);
    return /^01[016789]\d{7,8}$/.test(cleaned);
  };

  // 전화번호 중복 확인
  const checkPhoneAvailability = async () => {
    if (!isValidPhone(phone)) return;
    if (skipDuplicateCheck) {
      setPhoneAvailable(true);
      return;
    }

    try {
      const result = await verificationApi.checkPhone(normalizePhone(phone));
      setPhoneAvailable(result.available);
      if (!result.available) {
        setError('이미 가입된 휴대폰 번호입니다.');
      } else {
        setError('');
      }
    } catch {
      // 무시
    }
  };

  // 인증 코드 발송
  const sendCode = async () => {
    if (!isValidPhone(phone)) {
      setError('올바른 휴대폰 번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await verificationApi.send({
        type: 'PHONE',
        phone: normalizePhone(phone),
      });
      setStep('verify');
      setCountdown(180); // 3분
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 인증 코드 확인
  const verifyCode = async () => {
    if (code.length !== 6) {
      setError('6자리 인증번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await verificationApi.verify({
        type: 'PHONE',
        phone: normalizePhone(phone),
        code,
      });
      setStep('done');
      onVerified(result.verificationToken);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 재발송
  const resendCode = async () => {
    if (countdown > 0) return;
    await sendCode();
  };

  if (step === 'done') {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-ink-700">
          휴대폰 번호 <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="tel"
            value={formatPhone(phone)}
            disabled
            className="flex-1 px-3 py-2 border border-ink-300 rounded-control bg-ink-100 text-ink-700"
          />
          <span className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-control">
            <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            인증완료
          </span>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (code.length === 6 && !disabled && !isLoading) {
          verifyCode();
        }
      }
    };

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-ink-700">
            휴대폰 번호 <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formatPhone(phone)}
            disabled
            className="w-full px-3 py-2 border border-ink-300 rounded-control bg-ink-100 text-ink-700"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-ink-700">
            인증번호
          </label>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={handleKeyDown}
                disabled={disabled || isLoading}
                className="w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
                placeholder="6자리 입력"
              />
              {countdown > 0 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-red-500">
                  {formatCountdown(countdown)}
                </span>
              )}
            </div>
            <Button
              variant="primary"
              onClick={verifyCode}
              disabled={disabled || isLoading || code.length !== 6}
            >
              {isLoading ? '확인 중...' : '확인'}
            </Button>
          </div>
          <div className="flex justify-between items-center">
            <TextButton
              onClick={resendCode}
              disabled={disabled || isLoading || countdown > 0}
              className="text-sm"
            >
              {countdown > 0 ? `${formatCountdown(countdown)} 후 재발송 가능` : '인증번호 재발송'}
            </TextButton>
            <TextButton
              tone="neutral"
              onClick={() => {
                setStep('input');
                setCode('');
                setCountdown(0);
              }}
              className="text-sm"
            >
              번호 변경
            </TextButton>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-ink-700">
        휴대폰 번호 <span className="text-red-500">*</span>
      </label>
      <div className="flex space-x-2">
        <input
          type="tel"
          inputMode="numeric"
          value={formatPhone(phone)}
          onChange={readOnly ? undefined : (e) => {
            const normalized = normalizePhone(e.target.value);
            onPhoneChange(normalized.slice(0, 11));
            setPhoneAvailable(null);
            setError('');
          }}
          onBlur={readOnly ? undefined : checkPhoneAvailability}
          disabled={disabled || isLoading}
          readOnly={readOnly}
          maxLength={13}
          className={`flex-1 px-3 py-2 border rounded-control focus:ring-brand-500 focus:border-brand-500 ${
            readOnly
              ? 'bg-ink-100 text-ink-700 cursor-not-allowed'
              : error
              ? 'border-red-300'
              : phoneAvailable === true
              ? 'border-green-300'
              : 'border-ink-300'
          }`}
          placeholder="010-0000-0000"
        />
        <Button
          variant="primary"
          onClick={sendCode}
          disabled={disabled || isLoading || !isValidPhone(phone) || (!readOnly && !skipDuplicateCheck && phoneAvailable === false)}
          className="whitespace-nowrap"
        >
          {isLoading ? '발송 중...' : '인증번호 받기'}
        </Button>
      </div>
      {readOnly && (
        <p className="text-sm text-ink-500">초대받은 전화번호로 고정되어 있습니다.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!readOnly && !skipDuplicateCheck && phoneAvailable === true && !error && (
        <p className="text-sm text-green-600">사용 가능한 번호입니다.</p>
      )}
    </div>
  );
}
